import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

// Shared drag/pin/position mechanics for a floating panel. Currently not wired into any
// JSX (no onPointerDown/style/pin-toggle call site consumes this hook's return values) —
// the panels' actual sticky/pinned behavior is driven by a separate ResizeObserver-based
// system (desktopRailStyle/sideRailAnchorRef) elsewhere in SessionDetail.jsx. Kept dormant
// rather than deleted, mirroring the pre-extraction behavior exactly.
export const useFloatingPanel = (panelRef, { defaultPos, defaultSize }) => {
  const [floating, setFloating] = useState(false)
  const [dragging, setDragging] = useState(false)
  const [pos, setPos] = useState(defaultPos)
  const [size, setSize] = useState(defaultSize)
  const [pinned, setPinned] = useState(false)
  const dragState = useRef({ offsetX: 0, offsetY: 0 })
  const moveRafRef = useRef(null)
  const pendingPosRef = useRef(null)

  const style = useMemo(
    () => ({
      transform: `translate3d(${pos.x}px, ${pos.y}px, 0)`,
      width: size.width ? `${size.width}px` : undefined,
      transition: dragging ? 'none' : 'transform 160ms ease-out',
      willChange: 'transform',
    }),
    [pos.x, pos.y, size.width, dragging]
  )

  const onPointerDown = useCallback((event) => {
    if (event.button !== 0) return
    if (event.target.closest('button, input, textarea, select, a')) return
    const panel = panelRef.current
    if (!panel) return
    const rect = panel.getBoundingClientRect()
    dragState.current = { offsetX: event.clientX - rect.left, offsetY: event.clientY - rect.top }
    setPos({ x: rect.left, y: rect.top })
    setSize({ width: rect.width })
    setFloating(true)
    setDragging(true)
    event.preventDefault()
  }, [panelRef])

  const reset = useCallback(() => {
    setFloating(false)
    setDragging(false)
    setPos(defaultPos)
    setSize(defaultSize)
  }, [defaultPos, defaultSize])

  const togglePin = useCallback(() => {
    setPinned((prev) => {
      const next = !prev
      if (typeof window === 'undefined') return next
      const scrollX = window.scrollX || 0
      const scrollY = window.scrollY || 0
      if (next) {
        const panel = panelRef.current
        if (panel) {
          const rect = panel.getBoundingClientRect()
          setPos({ x: rect.left + scrollX, y: rect.top + scrollY })
          setSize({ width: rect.width })
        }
        setFloating(true)
        setDragging(false)
      } else {
        setPos((prevPos) => ({ x: prevPos.x - scrollX, y: prevPos.y - scrollY }))
      }
      return next
    })
  }, [panelRef])

  const revert = useCallback(() => {
    reset()
    setPinned(false)
  }, [reset])

  useEffect(() => {
    if (!dragging) return undefined
    const handleMove = (event) => {
      const { offsetX, offsetY } = dragState.current
      pendingPosRef.current = { x: event.clientX - offsetX, y: event.clientY - offsetY }
      if (moveRafRef.current) return
      moveRafRef.current = window.requestAnimationFrame(() => {
        if (pendingPosRef.current) {
          setPos(pendingPosRef.current)
        }
        moveRafRef.current = null
      })
    }
    const handleUp = () => setDragging(false)
    window.addEventListener('pointermove', handleMove)
    window.addEventListener('pointerup', handleUp)
    window.addEventListener('pointercancel', handleUp)
    const prevSelect = document.body.style.userSelect
    const prevCursor = document.body.style.cursor
    document.body.style.userSelect = 'none'
    document.body.style.cursor = 'grabbing'
    return () => {
      window.removeEventListener('pointermove', handleMove)
      window.removeEventListener('pointerup', handleUp)
      window.removeEventListener('pointercancel', handleUp)
      if (moveRafRef.current) {
        window.cancelAnimationFrame(moveRafRef.current)
        moveRafRef.current = null
      }
      pendingPosRef.current = null
      document.body.style.userSelect = prevSelect
      document.body.style.cursor = prevCursor
    }
  }, [dragging])

  const canRevert = floating || pos.x !== defaultPos.x || pos.y !== defaultPos.y

  return {
    isFloating: floating,
    isDragging: dragging,
    isPinned: pinned,
    canRevert,
    style,
    onPointerDown,
    togglePin,
    revert,
  }
}
